import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Download } from 'lucide-react';
import {
  tryParseIndividualSummaryJson,
  formatDigestPlainText,
  type ParsedIndividualSummary,
} from '../../utils/individualSummaryDigest';

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    console.error('Failed to copy:', e);
  }
}

function truncateExcerpt(text: string, max = 1200): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

export interface IndividualSummaryDigestPanelProps {
  rawSummary: string;
  /** Base filename without extension; `.txt` is appended with optional date. */
  downloadStem: string;
  /** Smaller controls for filing cards */
  compact?: boolean;
}

export const IndividualSummaryDigestPanel: React.FC<IndividualSummaryDigestPanelProps> = ({
  rawSummary,
  downloadStem,
  compact = false,
}) => {
  const [showRawJson, setShowRawJson] = useState(false);
  const digest = useMemo(() => tryParseIndividualSummaryJson(rawSummary), [rawSummary]);
  const digestPlain = useMemo(() => (digest ? formatDigestPlainText(digest) : ''), [digest]);

  useEffect(() => {
    setShowRawJson(false);
  }, [rawSummary]);

  const dateSuffix = new Date().toISOString().split('T')[0];
  const safeStem = downloadStem.replace(/[/\\?%*:|"<>]/g, '_');

  const btnClass = compact
    ? 'px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1'
    : 'px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1';

  const handleCopyDigest = () => void copyText(digestPlain);
  const handleCopyRaw = () => void copyText(rawSummary);
  const handleDownloadDigest = () =>
    downloadText(`${safeStem}_digest_${dateSuffix}.txt`, digestPlain);
  const handleDownloadRaw = () => downloadText(`${safeStem}_raw_${dateSuffix}.txt`, rawSummary);

  const showDigest = digest && !showRawJson;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {digest && (
          <button
            type="button"
            onClick={() => setShowRawJson((v) => !v)}
            className={btnClass}
          >
            {showRawJson ? 'Show digest' : 'Show raw JSON'}
          </button>
        )}
        {digest && (
          <button type="button" onClick={handleCopyDigest} className={btnClass} title="Copy digest">
            <Copy className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
            <span>Copy digest</span>
          </button>
        )}
        <button type="button" onClick={handleCopyRaw} className={btnClass} title="Copy raw response">
          <Copy className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
          <span>{digest ? 'Copy raw' : 'Copy'}</span>
        </button>
        {digest && (
          <button
            type="button"
            onClick={handleDownloadDigest}
            className={btnClass}
            title="Download digest as .txt"
          >
            <Download className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
            <span>Download digest</span>
          </button>
        )}
        <button
          type="button"
          onClick={handleDownloadRaw}
          className={btnClass}
          title="Download raw response as .txt"
        >
          <Download className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
          <span>{digest ? 'Download raw' : 'Download'}</span>
        </button>
      </div>

      <div className="p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
        {showDigest ? (
          <DigestBody digest={digest} />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">{rawSummary}</pre>
        )}
      </div>
    </div>
  );
};

function DigestBody({ digest }: { digest: ParsedIndividualSummary }) {
  const metaParts: string[] = [];
  if (digest.entity) metaParts.push(digest.entity);
  if (digest.totalPages != null) metaParts.push(`${digest.totalPages} pages`);
  if (digest.filename) metaParts.push(digest.filename);

  return (
    <div className="space-y-3">
      {metaParts.length > 0 && (
        <p className="text-sm text-gray-600 border-b border-gray-200 pb-2">
          {metaParts.join(' · ')}
        </p>
      )}
      {digest.verificationSummary && (
        <p className="text-sm text-gray-500">{digest.verificationSummary}</p>
      )}
      <ol className="list-decimal pl-5 space-y-4 text-sm text-gray-900">
        {digest.points.map((p, i) => (
          <li key={i} className="pl-1">
            <p className="font-medium text-gray-900">{p.text}</p>
            {(p.topic || p.pageLabel) && (
              <p className="mt-1 text-xs text-gray-500">
                {[p.topic, p.pageLabel].filter(Boolean).join(' · ')}
              </p>
            )}
            {p.excerpt && (
              <details className="mt-2 rounded-md border border-gray-200 bg-white/80">
                <summary className="cursor-pointer select-none px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
                  Supporting excerpt
                </summary>
                <div className="border-t border-gray-100 px-2 py-2 text-xs text-gray-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                  {truncateExcerpt(p.excerpt)}
                </div>
              </details>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
